import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";

import { closed } from "../../components/drawer/drawerSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { clearCallPurposeDetails } from "./callpurposeSlice";
import {
  createCallPurpose,
  getAllCallPurpose,
  getCallPurposeByID,
  updateCallPurpose,
} from "./services/callpurpose.service";

const CreateCallPurpose: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CallPurposeDetail = useAppSelector(
    (state) => state.callpurpose.CallPurposeDetail
  );
  const call_purpose_guid = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.callpurpose.formLoading);

  useEffect(() => {
    return () => {
      dispatch(clearCallPurposeDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (call_purpose_guid) {
      dispatch(getCallPurposeByID(call_purpose_guid));
    }
  }, [call_purpose_guid]);

  const handleSubmit = async (values: any) => {
    try {
      if (!call_purpose_guid) {
        const insertPayload = {
          call_purpose: values.call_purpose,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(createCallPurpose(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCallPurposeDetails());
          dispatch(getAllCallPurpose());
        }
      } else {
        const editPayload = {
          call_purpose_guid: call_purpose_guid,
          call_purpose: values.call_purpose,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(updateCallPurpose(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCallPurposeDetails());
          dispatch(getAllCallPurpose());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={CallPurposeDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "17px 0 8px",
              }}
            >
              <Typography.h4 className="m-0">
                {call_purpose_guid
                  ? "Update Call Purpose"
                  : "Create Call Purpose"}
              </Typography.h4>
              <Button
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </Button>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="call_purpose"
                label="Call Purpose"
                placeholder="Call Purpose"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"isactive"}
                component={FormSelectionField}
                label={"Status"}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={call_purpose_guid ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />

              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateCallPurpose;
